// compositionAgentConfig — the one canonical source for the approve /
// reject / requestRevision action wiring per composer agent (ADR-0002).
//
// Mirrors `findingAgentConfig` for composers. A per-agent
// composition-workbench wrapper reads its entry here and passes `actions`
// straight into the generic `<CompositionWorkbench>`. The workbench owns
// the mutation: it calls each action's `dispatch(id, reason?)` and pushes
// the configured success toast. Six of seven composer drafts route
// through the typed reviewable family (`/api/agent/reviewables/:kind/...`);
// SCRIVENER's suitability-letter-draft stays on the legacy named-action
// surface until Phase 5 migrates the comply contract.

import type { StaffActionResult } from '@septima/contract';

import { api, runStaffEndpoint } from '@/lib/api';
import {
    approveReviewable,
    rejectReviewable,
    requestRevisionReviewable,
} from '@/lib/agent-reviewables';
import type { ReviewableKind } from '@septima/contract';

/**
 * Every composer agent. One entry per composition-workbench wrapper file.
 * ATLAS's monthly-narrative composer keeps its bespoke surface in
 * `MonthlyNarrativePanel` / the review-item bodies; the seven brief-based
 * composers (ADR-0002) share the generic workbench.
 */
export type ComposerAgent =
    | 'SCRIVENER'
    | 'HERALD'
    | 'EMISSARY'
    | 'CHARTER'
    | 'HAVEN'
    | 'FORUM'
    | 'ARBITER';

/** Action verbs a reviewer can apply to a composer draft. */
export interface CompositionActionSpec {
    label: string;
    /** Dispatch this action against a draft row. */
    dispatch: (id: string, reason?: string) => Promise<StaffActionResult>;
    successToast: string;
}

export interface CompositionActions {
    approve: CompositionActionSpec;
    reject: CompositionActionSpec;
    requestRevision?: CompositionActionSpec;
}

import type { TableName } from '@/lib/db/schema';

export interface CompositionAgentConfigEntry {
    /** The `*_drafts` session-row table the draft lives in. */
    table: TableName;
    actions: CompositionActions;
}

/**
 * Build the three reviewable-family action specs for a composer draft
 * whose approve/reject/request-revision all route through the typed
 * `/api/agent/reviewables/:kind/:id/...` family.
 */
function reviewableCompositionActions(
    kind: ReviewableKind,
    opts: {
        approveLabel: string;
        approveToast: string;
        rejectToast: string;
        revisionLabel: string;
        revisionToast: string;
    },
): CompositionActions {
    return {
        approve: {
            label: opts.approveLabel,
            successToast: opts.approveToast,
            dispatch: (id) => approveReviewable(kind, id),
        },
        reject: {
            label: 'Reject',
            successToast: opts.rejectToast,
            dispatch: (id, reason) => rejectReviewable(kind, id, reason),
        },
        requestRevision: {
            label: opts.revisionLabel,
            successToast: opts.revisionToast,
            dispatch: (id, reason) =>
                requestRevisionReviewable(kind, id, reason ?? 'No reason given'),
        },
    };
}

/** Bespoke SCRIVENER dispatchers — materialise effects (writes SuitabilityLetter). */
const scrivenerApprove: CompositionActionSpec['dispatch'] = (id) =>
    runStaffEndpoint(
        api.complySuitability.approveSuitabilityLetterDraft({
            params: { id },
            body: {},
        }),
    );

const scrivenerReject: CompositionActionSpec['dispatch'] = (id, reason) =>
    runStaffEndpoint(
        api.complySuitability.rejectSuitabilityLetterDraft({
            params: { id },
            body: { reason },
        }),
    );

const scrivenerRevision: CompositionActionSpec['dispatch'] = (id, reason) =>
    runStaffEndpoint(
        api.complySuitability.requestRevisionSuitabilityLetterDraft({
            params: { id },
            body: { revisionReason: reason ?? 'No reason given' },
        }),
    ) as Promise<StaffActionResult>;

export const compositionAgentConfig: Record<ComposerAgent, CompositionAgentConfigEntry> = {
    // SCRIVENER's bespoke materialise effects live on the comply-suitability
    // contract (different from the generic reviewable family).
    SCRIVENER: {
        table: 'suitability_letter_drafts',
        actions: {
            approve: {
                label: 'Approve',
                dispatch: scrivenerApprove,
                successToast: 'Letter materialised',
            },
            reject: {
                label: 'Reject',
                dispatch: scrivenerReject,
                successToast: 'Draft rejected',
            },
            requestRevision: {
                label: 'Request revision',
                dispatch: scrivenerRevision,
                successToast: 'Sent back to SCRIVENER for revision',
            },
        },
    },
    HERALD: {
        table: 'onboarding_pack_drafts',
        actions: reviewableCompositionActions('onboarding-pack-draft', {
            approveLabel: 'Approve pack',
            approveToast: 'Onboarding pack issued',
            rejectToast: 'Pack draft rejected',
            revisionLabel: 'Request revision',
            revisionToast: 'Sent back to HERALD for revision',
        }),
    },
    EMISSARY: {
        table: 'client_communication_drafts',
        actions: reviewableCompositionActions('client-communication-draft', {
            approveLabel: 'Approve communication',
            approveToast: 'Communication approved',
            rejectToast: 'Communication rejected',
            revisionLabel: 'Request revision',
            revisionToast: 'Sent back to EMISSARY for revision',
        }),
    },
    CHARTER: {
        table: 'fund_report_drafts',
        actions: reviewableCompositionActions('fund-report-draft', {
            approveLabel: 'Approve report',
            approveToast: 'Fund report published',
            rejectToast: 'Report draft rejected',
            revisionLabel: 'Request revision',
            revisionToast: 'Sent back to CHARTER for revision',
        }),
    },
    HAVEN: {
        table: 'vulnerability_adaptation_plan_drafts',
        actions: reviewableCompositionActions('vulnerability-adaptation-plan-draft', {
            approveLabel: 'Approve plan',
            approveToast: 'Adaptation plan attached',
            rejectToast: 'Plan draft rejected',
            revisionLabel: 'Request revision',
            revisionToast: 'Sent back to HAVEN for revision',
        }),
    },
    FORUM: {
        table: 'governance_paper_drafts',
        actions: reviewableCompositionActions('governance-paper-draft', {
            approveLabel: 'Approve paper',
            approveToast: 'Governance paper finalised',
            rejectToast: 'Paper draft rejected',
            revisionLabel: 'Request revision',
            revisionToast: 'Sent back to FORUM for revision',
        }),
    },
    ARBITER: {
        table: 'fair_value_assessment_drafts',
        actions: reviewableCompositionActions('fair-value-assessment-draft', {
            approveLabel: 'Approve assessment',
            approveToast: 'Fair-value assessment finalised',
            rejectToast: 'Assessment draft rejected',
            revisionLabel: 'Request revision',
            revisionToast: 'Sent back to ARBITER for revision',
        }),
    },
};

/** Runtime exhaustiveness assertion — see `assertFindingConfigExhaustive`. */
export function assertCompositionConfigExhaustive(): ComposerAgent[] {
    const all: ComposerAgent[] = [
        'SCRIVENER',
        'HERALD',
        'EMISSARY',
        'CHARTER',
        'HAVEN',
        'FORUM',
        'ARBITER',
    ];
    const missing = all.filter((a) => !(a in compositionAgentConfig));
    if (missing.length > 0) {
        throw new Error(`compositionAgentConfig missing entries: ${missing.join(', ')}`);
    }
    return all;
}
